import React, { useRef, useCallback } from 'react';
import styles from './css/index.module.css';

const ResizableColumnHeader = ({ children, width, minWidth = 50, maxWidth = 1000, onResize, className = '', style = {} }) => {
    const headerRef = useRef(null);
    const isResizing = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    const handleMouseDown = useCallback(
        e => {
            e.preventDefault();
            e.stopPropagation();

            isResizing.current = true;
            startX.current = e.clientX;
            startWidth.current = width;

            // Add resizing class to prevent text selection
            if (headerRef.current) {
                headerRef.current.classList.add(styles.resizing);
            }

            // Add global event listeners
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            // Prevent text selection during resize
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';
        },
        [width],
    );

    const handleMouseMove = useCallback(
        e => {
            if (!isResizing.current) return;

            const deltaX = e.clientX - startX.current;
            const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth.current + deltaX));

            if (onResize) {
                onResize(newWidth);
            }
        },
        [minWidth, maxWidth, onResize],
    );

    const handleMouseUp = useCallback(() => {
        if (!isResizing.current) return;

        isResizing.current = false;

        // Remove resizing class
        if (headerRef.current) {
            headerRef.current.classList.remove(styles.resizing);
        }

        // Remove global event listeners
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Restore text selection
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, [handleMouseMove]);

    return (
        <div
            ref={headerRef}
            role="columnheader"
            className={`${styles.headerRowItem} ${styles.resizableHeader} ${className}`}
            style={{
                width: width,
                minWidth: width,
                maxWidth: width,
                ...style,
            }}>
            {children}
            <div className={styles.resizeHandle} onMouseDown={handleMouseDown} />
        </div>
    );
};

export default ResizableColumnHeader;
